import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { 
  Car, 
  Plus, 
  Pencil, 
  Trash2,
  Fuel,
  Calendar,
  Search
} from 'lucide-react';

const FUEL_TYPES = [
  { value: 'diesel', label: 'Gasóleo' },
  { value: 'gasolina', label: 'Gasolina' },
  { value: 'eletrico', label: 'Elétrico' },
  { value: 'hibrido', label: 'Híbrido' }
];

export default function VehiclesPage() {
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [vehicles, setVehicles] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [formData, setFormData] = useState({
    brand: '',
    model: '',
    plate: '',
    year: '',
    color: '',
    fuel_type: 'diesel',
    notes: ''
  });

  const isAdmin = user?.role === 'admin' || user?.role === 'gestor';

  useEffect(() => {
    loadVehicles();
  }, []);

  const loadVehicles = async () => {
    try {
      const response = await api().get('/vehicles');
      setVehicles(response.data);
    } catch (error) {
      toast.error('Erro ao carregar viaturas');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.brand || !formData.model || !formData.plate) {
      toast.error('Preencha os campos obrigatórios');
      return;
    }

    try {
      if (editingVehicle) {
        await api().put(`/vehicles/${editingVehicle.id}`, formData);
        toast.success('Viatura atualizada');
      } else {
        await api().post('/vehicles', formData);
        toast.success('Viatura adicionada');
      }
      setShowModal(false);
      setEditingVehicle(null);
      resetForm();
      loadVehicles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao guardar viatura');
    }
  };

  const handleEdit = (vehicle) => {
    setEditingVehicle(vehicle);
    setFormData({
      brand: vehicle.brand,
      model: vehicle.model,
      plate: vehicle.plate,
      year: vehicle.year || '',
      color: vehicle.color || '',
      fuel_type: vehicle.fuel_type || 'diesel',
      notes: vehicle.notes || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (vehicle) => {
    if (!window.confirm(`Tem certeza que deseja remover a viatura ${vehicle.plate}?`)) {
      return;
    }

    try {
      await api().delete(`/vehicles/${vehicle.id}`);
      toast.success('Viatura removida');
      loadVehicles();
    } catch (error) {
      toast.error('Erro ao remover viatura');
    }
  };

  const resetForm = () => {
    setFormData({
      brand: '',
      model: '',
      plate: '',
      year: '',
      color: '',
      fuel_type: 'diesel',
      notes: ''
    });
  };

  const openNewModal = () => {
    setEditingVehicle(null);
    resetForm();
    setShowModal(true);
  };

  const filteredVehicles = vehicles.filter(v => 
    v.brand?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    v.model?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    v.plate?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="vehicles-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Car className="h-6 w-6 text-amber-500" />
          <div>
            <h1 className="text-2xl font-bold font-[Manrope]">Viaturas</h1>
            <p className="text-muted-foreground text-sm">Gestão da frota de viaturas</p>
          </div>
        </div>
        
        {isAdmin && (
          <Button 
            onClick={openNewModal}
            className="bg-amber-500 hover:bg-amber-600 text-slate-900"
            data-testid="add-vehicle-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nova Viatura
          </Button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-amber-600">{vehicles.filter(v => v.active).length}</div>
            <p className="text-sm text-muted-foreground">Viaturas Ativas</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold">{vehicles.filter(v => v.fuel_type === 'diesel').length}</div>
            <p className="text-sm text-muted-foreground">Gasóleo</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold">{vehicles.filter(v => v.fuel_type === 'gasolina').length}</div>
            <p className="text-sm text-muted-foreground">Gasolina</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-slate-400">{vehicles.filter(v => !v.active).length}</div>
            <p className="text-sm text-muted-foreground">Inativas</p>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Pesquisar por marca, modelo ou matrícula..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Vehicles Table */}
      <Card>
        <CardContent className="p-0">
          {/* Mobile Cards */}
          <div className="block sm:hidden p-3 space-y-3">
            {filteredVehicles.map((vehicle) => (
              <div 
                key={vehicle.id} 
                className={`p-4 border rounded-lg ${!vehicle.active ? 'opacity-60 bg-slate-50' : 'bg-white'}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-amber-100 rounded-lg">
                      <Car className="h-5 w-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="font-semibold">{vehicle.brand} {vehicle.model}</p>
                      <p className="text-lg font-mono text-amber-600">{vehicle.plate}</p>
                    </div>
                  </div>
                  {!vehicle.active && (
                    <Badge variant="outline" className="text-slate-500">Inativa</Badge>
                  )}
                </div>
                <div className="mt-3 flex items-center gap-4 text-sm text-muted-foreground">
                  {vehicle.year && (
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {vehicle.year}
                    </span>
                  )}
                  <span className="flex items-center gap-1">
                    <Fuel className="h-3 w-3" />
                    {FUEL_TYPES.find(f => f.value === vehicle.fuel_type)?.label}
                  </span>
                </div>
                {isAdmin && vehicle.active && (
                  <div className="mt-3 flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => handleEdit(vehicle)}>
                      <Pencil className="h-3 w-3 mr-1" />
                      Editar
                    </Button>
                    <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDelete(vehicle)}>
                      <Trash2 className="h-3 w-3 mr-1" />
                      Remover
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Desktop Table */}
          <div className="hidden sm:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>VIATURA</TableHead>
                  <TableHead>MATRÍCULA</TableHead>
                  <TableHead>ANO</TableHead>
                  <TableHead>COMBUSTÍVEL</TableHead>
                  <TableHead>ESTADO</TableHead>
                  {isAdmin && <TableHead className="w-[100px]">AÇÕES</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredVehicles.map((vehicle) => (
                  <TableRow key={vehicle.id} className={!vehicle.active ? 'opacity-60' : ''}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-amber-100 rounded-lg">
                          <Car className="h-4 w-4 text-amber-600" />
                        </div>
                        <div>
                          <p className="font-medium">{vehicle.brand} {vehicle.model}</p>
                          {vehicle.color && (
                            <p className="text-xs text-muted-foreground">{vehicle.color}</p>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="font-mono text-amber-600 font-semibold">{vehicle.plate}</span>
                    </TableCell>
                    <TableCell>{vehicle.year || '-'}</TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        <Fuel className="h-3 w-3 mr-1" />
                        {FUEL_TYPES.find(f => f.value === vehicle.fuel_type)?.label}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {vehicle.active ? (
                        <Badge className="bg-emerald-100 text-emerald-800">Ativa</Badge>
                      ) : (
                        <Badge variant="outline" className="text-slate-500">Inativa</Badge>
                      )}
                    </TableCell>
                    {isAdmin && (
                      <TableCell>
                        {vehicle.active && (
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" onClick={() => handleEdit(vehicle)}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon" className="text-red-500" onClick={() => handleDelete(vehicle)}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {filteredVehicles.length === 0 && (
            <div className="text-center py-12 text-muted-foreground">
              <Car className="h-12 w-12 mx-auto mb-4 opacity-30" />
              <p>Nenhuma viatura encontrada</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="w-[95vw] max-w-[450px]">
          <DialogHeader>
            <DialogTitle className="font-[Manrope] flex items-center gap-2">
              <Car className="h-5 w-5 text-amber-500" />
              {editingVehicle ? 'Editar Viatura' : 'Nova Viatura'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="brand">Marca *</Label>
                <Input
                  id="brand"
                  value={formData.brand}
                  onChange={(e) => setFormData({...formData, brand: e.target.value})}
                  placeholder="Ex: Renault"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="model">Modelo *</Label>
                <Input
                  id="model"
                  value={formData.model}
                  onChange={(e) => setFormData({...formData, model: e.target.value})}
                  placeholder="Ex: Clio"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="plate">Matrícula *</Label>
                <Input
                  id="plate"
                  value={formData.plate}
                  onChange={(e) => setFormData({...formData, plate: e.target.value.toUpperCase()})}
                  placeholder="Ex: AA-00-BB"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="year">Ano</Label>
                <Input
                  id="year"
                  type="number"
                  value={formData.year}
                  onChange={(e) => setFormData({...formData, year: e.target.value})}
                  placeholder="Ex: 2022"
                  min="1990"
                  max="2030"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="color">Cor</Label>
                <Input
                  id="color"
                  value={formData.color}
                  onChange={(e) => setFormData({...formData, color: e.target.value})}
                  placeholder="Ex: Branco"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="fuel_type">Combustível</Label>
                <Select
                  value={formData.fuel_type}
                  onValueChange={(value) => setFormData({...formData, fuel_type: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FUEL_TYPES.map(fuel => (
                      <SelectItem key={fuel.value} value={fuel.value}>
                        {fuel.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notas</Label>
              <Input
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Observações..."
              />
            </div>

            <DialogFooter className="flex-col sm:flex-row gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-amber-500 hover:bg-amber-600 text-slate-900">
                {editingVehicle ? 'Guardar' : 'Adicionar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
